import { useEffect, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useInstallPrompt } from './useInstallPrompt';
import { getShellOverride, setShellOverride } from './useShellMode';

/**
 * The mobile shell's only settings surface.
 *
 * The desktop chrome that normally hosts these controls is not mounted here,
 * so without this sheet the shell override is a one-way door and /profile is
 * unreachable (tabIdFromPathname sends it to Alerts).
 */
export function MobileSettingsSheet({
  onClose,
}: {
  onClose: () => void;
}): ReactElement {
  const { canPrompt, isIos, promptInstall } = useInstallPrompt();
  const override = getShellOverride();

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="sr-mobile-sheet-backdrop"
      data-testid="sheet-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="sr-mobile-sheet" role="dialog" aria-modal="true" aria-label="Settings">
        <div className="sr-mobile-sheet-head">
          <span>Settings</span>
          <button type="button" className="sr-mobile-sheet-close" onClick={onClose}>
            Done
          </button>
        </div>

        <div className="sr-mobile-sheet-section">Layout</div>
        <button
          type="button"
          className="sr-mobile-sheet-row"
          aria-pressed={override === 'auto'}
          onClick={() => setShellOverride('auto')}
        >
          Automatic {override === 'auto' ? '●' : ''}
        </button>
        <button
          type="button"
          className="sr-mobile-sheet-row"
          aria-pressed={override === 'desktop'}
          onClick={() => setShellOverride('desktop')}
        >
          Desktop layout {override === 'desktop' ? '●' : ''}
        </button>

        {(canPrompt || isIos) && (
          <>
            <div className="sr-mobile-sheet-section">App</div>
            {canPrompt ? (
              <button
                type="button"
                className="sr-mobile-sheet-row"
                onClick={() => void promptInstall()}
              >
                Add to home screen
              </button>
            ) : (
              <div className="sr-mobile-sheet-note">
                On iOS Safari: Share → <strong>Add to Home Screen</strong>
              </div>
            )}
          </>
        )}

        <div className="sr-mobile-sheet-section">Account</div>
        <Link className="sr-mobile-sheet-row" to="/profile" onClick={onClose}>
          Profile and subscription
        </Link>
      </div>
    </div>
  );
}
